import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CustomHandler } from "@tSurya/Store/CustomHandlerCreator/CustomHandlerCreator";

type Props = {
    product?: ProductModel;
    baselineProperties?: Record<string, string>;
    mergedProperties?: Record<string, string>;
};

type HandlerType = CustomHandler<{}, Props>;

/**
 * Locates the PDP product (in the core Data/Products store) that backs the
 * currently configured product. The configurator stores the ERP product
 * number, while the PDP product is keyed by GUID, so we match on productNumber.
 */
export const FindPdpProduct: HandlerType = props => {
    var state = props.getState();
    var configuratorStore = state.SURData.configuratorStore;

    // itemAttributes === null means the override payload hasn't been fetched yet.
    // Skip silently; the chain will run again once the fetch completes.
    if (configuratorStore.itemAttributes == null) {
        return false;
    }

    var productNumber = configuratorStore.productConfig?.productId;
    if (!productNumber) {
        return false;
    }

    var byId = state.data.products.byId;
    var product = Object.keys(byId)
        .map(id => byId[id])
        .find(p => p?.productNumber === productNumber);

    if (!product) {
        return false;
    }

    props.product = product;
};

/**
 * Captures the product's original properties the first time overrides are
 * applied. Subsequent runs reuse this snapshot so overrides always merge onto
 * a clean baseline rather than compounding previous overrides.
 */
export const SnapshotBaseline: HandlerType = props => {
    var configuratorStore = props.getState().SURData.configuratorStore;
    var baseline = configuratorStore.baselineProductProperties;

    if (baseline == null) {
        baseline = { ...(props.product!.properties ?? {}) };
        props.dispatch({
            type: "Custom/Configurator/SetBaselineProductProperties",
            baselineProductProperties: baseline,
        });
    }

    props.baselineProperties = baseline;
};

/**
 * Merges the resolved item's attribute overrides onto the baseline. When the
 * resolved item has no overrides, the baseline is restored as-is, clearing any
 * stale values from a previously resolved item.
 */
export const MergeOverrides: HandlerType = props => {
    var configuratorStore = props.getState().SURData.configuratorStore;
    var resolvedItemId = configuratorStore.configurationResult?.resolvedItemId;
    var itemAttributes = configuratorStore.itemAttributes ?? [];

    var overrides = resolvedItemId ? itemAttributes.find(x => x.itemId === resolvedItemId)?.attributes : undefined;

    props.mergedProperties = {
        ...props.baselineProperties,
        ...(overrides ?? {}),
    };
};

/**
 * Re-dispatches the product into the core store with patched properties so all
 * existing PDP widgets (which read product.properties) re-render with the
 * item-specific values.
 */
export const PatchProduct: HandlerType = props => {
    var patched: ProductModel = {
        ...props.product!,
        properties: props.mergedProperties!,
    };

    props.dispatch({
        type: "Data/Products/CompleteLoadProduct",
        model: patched,
    });
};

export const chain = [FindPdpProduct, SnapshotBaseline, MergeOverrides, PatchProduct];

const applyItemAttributeOverrides = createHandlerChainRunner(chain, "ApplyItemAttributeOverrides");
export default applyItemAttributeOverrides;
